/**
 * Auth-Context + Router-Guard für takeoff.bidi.pro (2026-05-05).
 *
 * Lädt beim App-Start `/api/auth/me` und entscheidet:
 *   - 200 + `authenticated:true` → User ist eingeloggt, App rendert normal.
 *   - 401 → User wird zu `/login` umgeleitet.
 *   - "auth-disabled"-Mode → Bypass (für Dev-Builds ohne AUTH_*-env).
 *
 * Login-Page postet Username + Passwort an `/api/auth/login`. Bei Erfolg
 * setzt das Backend ein HttpOnly-JWT-Cookie; das Frontend reloadet `/me`
 * und hat Zugriff auf alle weiteren `/api/...`-Routes.
 *
 * Logout-Button löscht das Cookie via `/api/auth/logout` und reloadet die
 * Login-Page.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { Navigate, useLocation } from "react-router-dom";

type AuthState =
  | { status: "loading" }
  | { status: "authenticated"; username: string; mode?: string }
  | { status: "anonymous" };

interface AuthContextValue {
  state: AuthState;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  login: (
    username: string,
    password: string
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function fetchMe(): Promise<AuthState> {
  try {
    const res = await fetch("/api/auth/me", { credentials: "include" });
    if (!res.ok) return { status: "anonymous" };
    const data = (await res.json()) as {
      authenticated?: boolean;
      username?: string;
      mode?: string;
    };
    if (data.authenticated && data.username) {
      return {
        status: "authenticated",
        username: data.username,
        mode: data.mode,
      };
    }
    return { status: "anonymous" };
  } catch {
    return { status: "anonymous" };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  useEffect(() => {
    fetchMe().then(setState);
  }, []);

  const refresh = async () => {
    setState({ status: "loading" });
    setState(await fetchMe());
  };

  const logout = async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch {
      /* ignore — Cookie wird trotzdem clientseitig nicht mehr akzeptiert */
    }
    setState({ status: "anonymous" });
  };

  const login = async (
    username: string,
    password: string
  ): Promise<{ ok: true } | { ok: false; error: string }> => {
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        return {
          ok: false,
          error: data.error ?? `Login fehlgeschlagen (${res.status})`,
        };
      }
      await refresh();
      return { ok: true };
    } catch (err) {
      return {
        ok: false,
        error:
          err instanceof Error ? err.message : "Login fehlgeschlagen (Netzwerk)",
      };
    }
  };

  return (
    <AuthContext.Provider value={{ state, refresh, logout, login }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth muss innerhalb von <AuthProvider> stehen");
  return ctx;
}

/**
 * Wrapper-Component für geschützte Routes. Zeigt Lade-Indikator während
 * der initiale `/me`-Check läuft, leitet bei `anonymous` auf `/login` um
 * und merkt sich die ursprüngliche URL als `next`-Param.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { state } = useAuth();
  const location = useLocation();
  if (state.status === "loading") {
    return (
      <div
        className="flex items-center justify-center min-h-screen"
        style={{ background: "var(--bd-bg)" }}
      >
        <div style={{ color: "var(--bd-text-3)", fontSize: 13 }}>
          Lade Bidi Take-off …
        </div>
      </div>
    );
  }
  if (state.status === "anonymous") {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <>{children}</>;
}
